<script setup lang="ts">
/*
* Компонент LinkCard - карточка отображения полезной ссылки
*
* Основные функции:
* - Отображение информации о полезной ссылке (название, описание, логотип)
* - Переход по ссылке при клике на карточку
* - Управление статусом избранного для ссылки
* - Визуальное отображение состояния "в избранном"
*
* Особенности:
* - Поддержка логотипов ссылок с fallback на изображение по умолчанию
* - Открытие ссылок в новой вкладке браузера
* - Изолированная обработка кликов по кнопке избранного
*/

// Импорты Vue
import { computed } from 'vue'

// Импорты изображений
import emptyLink from '@images/links/emptyLink.jpg'

// Импорты типов
import type { IUsefulLink } from '@/modules/links/types/IUsefulLink'

// Определение props компонента
const props = defineProps<{ link: IUsefulLink }>()

// Определение emits компонента
const emit = defineEmits<{
  (e: 'toggle-favorite', id: string): void // Событие переключения статуса избранного
}>()

/**
 * Вычисляемое свойство для определения CSS-класса кнопки избранного
 *
 * Назначение:
 * - Динамически определяет класс кнопки в зависимости от статуса избранного
 * - Обеспечивает различное визуальное отображение для разных состояний
 *
 * Возвращает:
 * - string: CSS-класс для кнопки избранного
 *   - 'link-card__fav-btn--favorite' если ссылка в избранном
 *   - 'link-card__fav-btn--not-favorite' если ссылка не в избранном
 */
const btnClass = computed(() => {
  return props.link.favorit ? 'link-card__fav-btn--favorite' : 'link-card__fav-btn--not-favorite'
})

/**
 * Обработчик переключения статуса избранного для ссылки
 *
 * Назначение:
 * - Эмитит событие переключения избранного с ID ссылки
 * - Используется для синхронизации состояния с родительским компонентом
 *
 * Особенности:
 * - Вызывается с prevent для изоляции от основного клика по карточке
 * - Использует @click.stop для предотвращения всплытия события
 */
function onToggle() {
  emit('toggle-favorite', props.link.id)
}

/**
 * Функция открытия URL ссылки в новой вкладке
 *
 * Назначение:
 * - Выполняет переход по URL ссылки в новом окне браузера
 * - Проверяет наличие URL перед выполнением перехода
 *
 * Входные данные:
 * - url: string - URL адрес для открытия
 *
 * Особенности:
 * - Использует window.open с '_blank' для открытия в новой вкладке
 * - Не выполняет действий если URL отсутствует или пустой
 */
const openUrl = (url: string) => {
  if (!url) return
  window.open(url, '_blank')
}
</script>

<template>
  <div class="link-card d-flex align-center justify-space-between pa-4 cursor-pointer" :class="{ 'link-card--custom': link.is_pinned }" @click="openUrl(link.url)">
    <div class="d-flex align-center gap-3">
      <VImg
        :src="link.logo || emptyLink"
        width="40"
        height="40"
      />
      <div class="d-flex flex-column">
        <div class="link-card__title body-1">
          {{ link.name }}
        </div>
        <div class="link-card__description body-1">
          {{ link.description }}
        </div>
      </div>
    </div>

    <VIconBtn
      v-if="!link.is_pinned"
      class="link-card__fav-btn"
      variant="plain"
      :class="btnClass"
      icon="ri-star-s-fill"
      @click.prevent="onToggle"
      @click.stop
    />
  </div>
</template>

<style scoped lang="scss">
.link-card {
  border-radius: 6px;

  &__title {
    color: rgb(var(--v-theme-text-primary), var(--v-high-emphasis-opacity));
  }

  &__description {
    color: rgb(var(--v-theme-text-secondary), var(--v-medium-emphasis-opacity));
  }

  &:hover {
    background-color: rgb(var(--v-theme-primary), var(--v-selected-opacity));
  }

  &--custom {
    // фон всегда secondary selected-opacity
    background-color: rgb(var(--v-theme-secondary), var(--v-selected-opacity)) !important;

    // убрать hover
    &:hover {
      background-color: none !important;
    }

    .link-card__title {
      // primary без прозрачности
      color: rgb(var(--v-theme-primary)) !important;
    }

    .link-card__description {
      // secondary + medium opacity
      color: rgb(var(--v-theme-text-secondary), var(--v-medium-emphasis-opacity)) !important;
    }
  }

  &__fav-btn {
    &--not-favorite {
      color: rgb(var(--v-theme-text-primary), 0.3) !important;

      &:hover {
        color: rgb(var(--v-theme-primary)) !important;
      }
    }

    &--favorite {
      color: rgb(var(--v-theme-primary)) !important;
    }
  }
}
</style>
